import React, { FC, useRef, useState } from 'react';
import './ExportUI.css';
import { ExecuteButton, ExportButton, FaEye, FaFilePdf, GenerateUIFile, Modal, ModalData } from '@/Shared';
import { ExportUIData } from '@/Shared';
import { DOCUMENT_FORM_REGISTRY } from '@/Domains';

type Mode = 'select' | 'generate' | 'export';

interface ExportUIProps {
	onClose: () => void;
	data?: ExportUIData | null;
}


/**
 * ExportUI
 *
 * Acts as a small UI state machine:
 * - select mode: choose document type
 * - generate mode: dynamic form per document type
 * - export mode: list existing generated documents
 */
const ExportUI: FC<ExportUIProps> = ({ data, onClose }) => {

	const [mode, setMode] = useState<Mode>('select');
	const [selectedFile, setSelectedFile] = useState<GenerateUIFile | null>(null);

	const handleGoBack = () => {
		return mode === "select"
			? undefined
			: () => setMode('select');
	}


	/**
	 * Step 1: Selection screen (generate / export entry point)
	 */
	const renderSelect = () => (<>
		{/* GENERATE SECTION */}
		<fieldset className="generate-section">
			<legend>Générer un document</legend>
			<div className='flex-row'>
				{data?.toGenerate.map(file => (
					<ExecuteButton
						key={file.label}
						label={file.label}
						onClick={() => {
							if((data.noFormDocTypes ?? []).includes(file.type)) {
								onClose();
								file.handler();
							}
	
							else {
								setSelectedFile(file);
								setMode('generate');
							}
						}}
						icon={<FaFilePdf />}
					/>
				))}
			</div>
		</fieldset>

		{/* EXPORT SECTION */}
		<fieldset className="export-section">
			<legend>Documents générés</legend>

			<ExecuteButton
				label="Voir les exports"
				onClick={() => setMode('export')}
				icon={<FaEye />}
			/>
		</fieldset>
	</>);


	const FormComponent = DOCUMENT_FORM_REGISTRY[selectedFile?.type as keyof typeof DOCUMENT_FORM_REGISTRY];

	/**
	 * Step 2: Generate form (dynamic per document type)
	 */
	const renderGenerate = () => (<>
		<div className='form-header'>
			<h3>{selectedFile?.label}</h3>
		</div>

		{/* Replace with real form component */}
		{data && selectedFile &&
			<>
				{
					FormComponent ? (
						<FormComponent
							document={selectedFile}
							handleClose={onClose}
							handleCancel={() => setMode('select')}
							id={data.id}
						/>
					)
						: <div>Aucun formulaire disponible pour ce type de document.</div>
				}
			</>
		}
	</>);

	/**
	 * Step 3: Export list
	 */
	const renderExport = () => (<>
		<div className='form-header'>
			<h3>Documents générés</h3>
		</div>
		<fieldset>

			<table>
				<thead>
					<tr><th>Documents</th><th>Actions</th></tr>
				</thead>
				<tbody>
					{(data?.toDownload ?? []).map((file, index) => (
						<tr key={index + '_' + file.type} className='export-row'>
							<td className='text-left'>{file.label}</td>
							<td>

								<ExportButton
									onClick={() => {
										file.handlers?.export?.();
										onClose();
									}}
								/>
							</td>
						</tr>
					))}
					{
						(data?.toDownload ?? []).length === 0 && (
							<tr><td colSpan={2}>Aucun élément disponible.</td> </tr>
						)
					}
				</tbody>
			</table>
		</fieldset>
	</>);

	/**
	 * Render current mode
	 */
	const content = (
		<div className={`ExportUI-content ${selectedFile?.type}`} onClick={e => e.stopPropagation()}>
			{
				mode === 'select'
					? renderSelect()
					: mode === 'generate'
						? renderGenerate()
						: renderExport()
			}
		</div>
	);


	const modalData: ModalData | undefined = data ? { title: "Module d'export", content } : undefined;

	return <Modal data={modalData} onClose={onClose} onBack={handleGoBack()} />;
};

export default ExportUI;